import { Users, Building2, Banknote, FlaskConical, ClipboardCheck } from "lucide-react";
import type { LucideIcon } from "lucide-react";
import { useLanguage } from "@/hooks/useLanguage";

interface WebinarAudienceProps {
  title: string;
  items: string[];
}

const PERSONA_ICONS: LucideIcon[] = [Users, Building2, Banknote, FlaskConical, ClipboardCheck];

const WebinarAudience = ({ title, items }: WebinarAudienceProps) => {
  const lang = useLanguage();
  const isEs = lang === "es";
  const isZh = lang === "zh-cn";
  if (items.length === 0 && !title) return null;

  return (
    <section className="py-20 sm:py-28 bg-primary text-primary-foreground">
      <div className="container mx-auto px-6 lg:px-10 max-w-5xl">
        <div className="text-center mb-12">
          <p className="text-xs font-bold uppercase tracking-wider text-accent mb-2">
            {isZh ? "这是为您准备的吗？" : isEs ? "¿Es para ti?" : "Is this for you?"}
          </p>
          <h2 className="text-2xl sm:text-3xl font-bold">{isZh ? "适合参加人员" : isEs ? "¿A quién va dirigido?" : "Who should attend?"}</h2>
          {title && (
            <p
              className="mt-4 text-primary-foreground/60 max-w-2xl mx-auto leading-relaxed"
              dangerouslySetInnerHTML={{
                __html: title.replace(/\*\*(.*?)\*\*/g, '<strong class="text-primary-foreground font-semibold">$1</strong>'),
              }}
            />
          )}
        </div>

        <div className={`grid sm:grid-cols-2 auto-rows-fr ${items.length !== 4 ? 'lg:grid-cols-3' : 'lg:grid-cols-2 max-w-3xl mx-auto'} gap-5`}>
          {items.map((item, i) => {
            const Icon = PERSONA_ICONS[i % PERSONA_ICONS.length];
            // Extract the bold part as title if present
            const boldMatch = item.match(/^(.+?)\s+who\b/i);
            const persona = boldMatch ? boldMatch[1] : item;
            const detail = boldMatch ? item.slice(boldMatch[1].length) : "";

            return (
              <div
                key={i}
                className="h-full bg-primary-foreground/[0.06] border border-primary-foreground/10 rounded-xl p-6 backdrop-blur-sm hover:bg-primary-foreground/[0.1] transition-colors"
              >
                <Icon size={24} strokeWidth={1.5} className="text-accent mb-4" />
                <p
                  className="text-sm text-primary-foreground/90 leading-relaxed"
                  dangerouslySetInnerHTML={{
                    __html: item.replace(
                      /\*\*(.*?)\*\*/g,
                      '<strong class="text-primary-foreground font-semibold">$1</strong>'
                    ),
                  }}
                />
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default WebinarAudience;
